import { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { ChevronLeft, HelpOutlineOutlined, MailOutlineOutlined, QuestionAnswerOutlined } from '@mui/icons-material'
import { Button, ButtonBase, Grid, IconButton, Typography } from '@mui/material'
import { useBooleanFlagValue, useWhenProviderReady } from '@openfeature/react-sdk'

import NeedHelpModal from '../../components/need-help-modal'
import DashboardScreen from '../../components/screen/dashboard-screen'
import StickyHeader from '../../components/sticky-header'
import genericAvatarImg from '../../images/generic-avatar.svg'
import alertsContactsImg from '../../images/help/alerts-contacts.svg'
import keepSensorsOnlineImg from '../../images/help/keep-sensors-online.svg'
import maintenanceTipsImg from '../../images/help/maintenance-tips.svg'
import severeWeatherPreparednessImg from '../../images/help/severe-weather-preparedness.svg'
import { AuthenticationContext } from '../../providers/auth-provider'
import { ChatContext } from '../../providers/chat-provider'
import { DashboardContext } from '../../providers/dashboard-provider'
import { NavContext } from '../../providers/nav-provider'
import {
  CHAT_BUTTON_CONTAINER_ID,
  CHAT_CHANNEL_NAME,
  CHAT_CHANNEL_UUID,
  is8x8ChatActive,
  load8x8Chat,
  waitFor8x8ChatButton,
  watch8x8ChatAvailability,
} from '../../utils/8x8-chat'
import { formatAddress } from '../../utils/helpers'
import useIsMobile from '../../utils/hooks/useIsMobile'

import useStyles from './index.styles'

const CONTACT_SENSOR_SUPPORT_URL = 'http://resources.meshify.com/contact'

type PrefilledVars = Record<string, string>

const Help = () => {
  const classes = useStyles
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { setMoreMenuOpen } = useContext(NavContext)
  const { ownUser } = useContext(AuthenticationContext)
  const { startChat } = useContext(ChatContext)
  const { selectedFolderId, folder, folderAttributes } = useContext(DashboardContext)
  const isFeatureFlagProviderReady = useWhenProviderReady()
  const is8x8ChatEnabled = useBooleanFlagValue('8x8-chat-protect-web', false)

  const [helpModalOpen, setHelpModalOpen] = useState<boolean>(false)
  const [chatLoading, setChatLoading] = useState<boolean>(true)
  const [chatAvailable, setChatAvailable] = useState<boolean>(false)

  const prefilledVars = useMemo<PrefilledVars>(
    () => ({
      Name: `${ownUser?.information?.first || ''} ${ownUser?.information?.last || ''}`.trim(),
      'Phone Number': String(ownUser?.phone || ''),
      'Account Number': String(folderAttributes?.clientAccountNumber || ''),
      'Location Street Address': formatAddress(folder?.information.address.address ?? '', folder?.information.address.country),
      'Location City, State and Zip': formatAddress(
        `${folder?.information.address.locality ?? ''}, ${folder?.information.address.region ?? ''} ${folder?.information.address.code ?? ''}`,
        folder?.information.address.country
      ),
    }),
    [
      folder?.information.address.address,
      folder?.information.address.code,
      folder?.information.address.country,
      folder?.information.address.locality,
      folder?.information.address.region,
      folderAttributes?.clientAccountNumber,
      ownUser?.information?.first,
      ownUser?.information?.last,
      ownUser?.phone,
    ]
  )

  // Initialize 8x8 only after feature flags and account data are ready, track live launcher availability,
  // and stop the observer when the Help screen unmounts or its chat configuration changes.
  useEffect(() => {
    if (!isFeatureFlagProviderReady || !is8x8ChatEnabled || !ownUser || (selectedFolderId && (!folder || !folderAttributes))) {
      // Account data for a newly selected folder is still loading; don't show stale availability from the previous account.
      setChatLoading(true)
      return () => undefined
    }

    let isSubscribed = true
    const stopWatchingChatAvailability = watch8x8ChatAvailability((chatButtonAvailable) => {
      if (!isSubscribed) return

      setChatAvailable(chatButtonAvailable || is8x8ChatActive())
    })

    load8x8Chat(
      {
        channelName: CHAT_CHANNEL_NAME,
        channelUuid: CHAT_CHANNEL_UUID,
        prefilledVars,
      },
      async () => {
        const chatButtonAvailable = await waitFor8x8ChatButton()

        if (!isSubscribed) return

        setChatAvailable(chatButtonAvailable || is8x8ChatActive())
        setChatLoading(false)
      },
      () => {
        if (!isSubscribed) return
        setChatAvailable(is8x8ChatActive())
        setChatLoading(false)
      }
    )

    return () => {
      isSubscribed = false
      stopWatchingChatAvailability()
    }
  }, [folder, folderAttributes, is8x8ChatEnabled, isFeatureFlagProviderReady, ownUser, prefilledVars, selectedFolderId])

  const handleChatClick = () => {
    if (!chatAvailable) return

    startChat()
  }

  const supportButtonProps =
    chatAvailable === false
      ? {
          component: 'a' as const,
          href: CONTACT_SENSOR_SUPPORT_URL,
          target: '_blank',
          rel: 'noopener noreferrer',
          startIcon: <MailOutlineOutlined />,
        }
      : {
          onClick: handleChatClick,
          startIcon: <QuestionAnswerOutlined />,
        }

  return (
    <DashboardScreen
      headerProps={{
        showLogo: true,
        leftIcon: isMobile ? (
          <IconButton
            size="small"
            color="secondary"
            onClick={() => {
              navigate(-1)
              setMoreMenuOpen(true)
            }}
          >
            <ChevronLeft />
          </IconButton>
        ) : undefined,
      }}
      data-testid="help-screen"
    >
      <Grid container={true} size={12} sx={classes.container}>
        {!isMobile && (
          <StickyHeader marginBottom={1}>
            <Typography variant="body2" sx={classes.desktopHeader}>
              <HelpOutlineOutlined /> Help & Support
            </Typography>
          </StickyHeader>
        )}
        <Grid container={true} size={12} sx={classes.contentContainer}>
          <Grid container={true} size={12} sx={classes.linkContainer}>
            <ButtonBase component="a" href="https://resources.meshify.com/help/#sensor-health" target="_blank" rel="noopener noreferrer">
              <Grid container={true} size={12} sx={classes.helpLinkGrid}>
                <Typography variant="body1" sx={classes.helpText}>
                  How to keep your sensors online
                </Typography>
                <Grid>
                  <img src={keepSensorsOnlineImg} alt="" width={80} height={80} />
                </Grid>
              </Grid>
            </ButtonBase>
            <ButtonBase component="a" href="https://resources.meshify.com/help/#alerts" target="_blank" rel="noopener noreferrer">
              <Grid container={true} size={12} sx={classes.helpLinkGrid}>
                <Typography variant="body1" sx={classes.helpText}>
                  All about alerts and contacts
                </Typography>
                <Grid>
                  <img src={alertsContactsImg} alt="" width={80} height={80} />
                </Grid>
              </Grid>
            </ButtonBase>
            <ButtonBase component="a" href="https://resources.meshify.com/help/#maintenance" target="_blank" rel="noopener noreferrer">
              <Grid container={true} size={12} sx={classes.helpLinkGrid}>
                <Typography variant="body1" sx={classes.helpText}>
                  Maintenance tips for success
                </Typography>
                <Grid>
                  <img src={maintenanceTipsImg} alt="" width={80} height={80} />
                </Grid>
              </Grid>
            </ButtonBase>
            <ButtonBase component="a" href="https://resources.meshify.com/help/#weather" target="_blank" rel="noopener noreferrer">
              <Grid container={true} size={12} sx={classes.helpLinkGrid}>
                <Typography variant="body1" sx={classes.helpText}>
                  Severe weather preparedness
                </Typography>
                <Grid>
                  <img src={severeWeatherPreparednessImg} alt="" width={80} height={80} />
                </Grid>
              </Grid>
            </ButtonBase>
          </Grid>
          <Grid container={true} size={12} sx={classes.supportButtonContainer}>
            {!isFeatureFlagProviderReady ? null : is8x8ChatEnabled ? (
              <>
                <div id={CHAT_BUTTON_CONTAINER_ID} style={classes.hiddenChatButtonContainer}></div>
                {!chatLoading && (
                  <>
                    <Button variant="contained" color="secondary" size="large" disableElevation fullWidth {...supportButtonProps}>
                      {chatAvailable ? 'Chat with Sensor Support' : 'Contact Support'}
                    </Button>
                    {chatAvailable && (
                      <Typography variant="body1" color="secondary" sx={classes.chatAvailabilityText}>
                        Chat support available Mon - Fri
                        <br />
                        8:30am - 7:30pm Eastern Time
                      </Typography>
                    )}
                  </>
                )}
              </>
            ) : (
              <Button
                variant="contained"
                size="large"
                disableElevation
                onClick={() => setHelpModalOpen(true)}
                sx={classes.requestHelpButton}
                startIcon={<img src={genericAvatarImg} alt="" width={24} height={24} />}
              >
                Request Help
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>
      <NeedHelpModal isOpen={helpModalOpen} onClose={() => setHelpModalOpen(false)} supportLevel={folder?.theme?.supportLevel as string} />
    </DashboardScreen>
  )
}

export default Help
